// v10/src/components/EmergencyResults.js
// v1.2 — PDF profesional con diseño Condor360
import React from 'react';
import { motion } from 'framer-motion';
import { Award, Clock, AlertTriangle, RotateCcw, ArrowLeft, Download } from 'lucide-react';
import jsPDF from 'jspdf';

const SCORE_MSG = {
  90: 'Excelente. Procedimiento dominado.',
  80: 'Muy bien. Seguí practicando para mejorar el tiempo.',
  60: 'Aprobado. Revisá los pasos con errores de orden.',
  0:  'Necesitás más práctica. Hablá con tu instructor.',
};

const getScoreMsg = (score) => {
  if (score >= 90) return SCORE_MSG[90];
  if (score >= 80) return SCORE_MSG[80];
  if (score >= 60) return SCORE_MSG[60];
  return SCORE_MSG[0];
};

const getScoreColor = (score) => {
  if (score >= 80) return '#22c55e';
  if (score >= 60) return '#f59e0b';
  return '#ef4444';
};

// ── Helper: hex a RGB ─────────────────────────────────────────────
const hexToRgb = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
};

const EmergencyResults = ({ results, onRetry, onClose, darkMode }) => {
  if (!results) {
    return (
      <div style={{ position:'fixed', inset:0, zIndex:9999, background:'#0a0f1e', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <div style={{ textAlign:'center', color:'white' }}>
          <div style={{ fontSize:48, marginBottom:16 }}>🚨</div>
          <p style={{ fontSize:15, fontWeight:600, color:'rgba(255,255,255,0.6)' }}>Error al cargar resultados</p>
          <button onClick={onClose} style={{ marginTop:16, padding:'12px 24px', borderRadius:14, border:'none', background:'rgba(255,255,255,0.1)', color:'white', fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>
            Volver
          </button>
        </div>
      </div>
    );
  }

  const { score, total_seconds, benchmark_seconds, order_errors, self_errors = 0, item_times, procedure_title, aircraft_registration, pilot_name } = results;
  const total_errors = order_errors + self_errors;
  const scoreColor = getScoreColor(score);
  const timeDiff = total_seconds - benchmark_seconds;

  // ── PDF Profesional — fondo blanco ──────────────────────────────
  const exportPDF = () => {
    try {
      const doc = new jsPDF({ unit: 'mm', format: 'a4' });
      const W = 210;
      const margin = 14;
      const contentW = W - margin * 2;

      // ── Header rojo ──────────────────────────────────────────────
      doc.setFillColor(239, 68, 68);
      doc.rect(0, 0, W, 32, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('CONDOR360', margin, 12);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text('Emergency Procedure Trainer', margin, 19);
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text(procedure_title || 'Procedimiento de emergencia', margin, 27);

      // ── Info vuelo ───────────────────────────────────────────────
      doc.setFillColor(241, 245, 249);
      doc.roundedRect(margin, 38, contentW, 22, 3, 3, 'F');
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(37, 99, 235);
      doc.text('DATOS DEL VUELO', margin + 4, 46);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(50, 50, 50);
      doc.setFontSize(9);
      doc.text(`Aeronave: `, margin + 4, 53);
      doc.setFont('helvetica', 'bold');
      doc.text(aircraft_registration || '—', margin + 24, 53);
      doc.setFont('helvetica', 'normal');
      doc.text(`Piloto: `, margin + 70, 53);
      doc.setFont('helvetica', 'bold');
      doc.text(pilot_name || '—', margin + 84, 53);
      doc.setFont('helvetica', 'normal');
      doc.text(`Fecha: `, margin + 130, 53);
      doc.setFont('helvetica', 'bold');
      doc.text(new Date().toLocaleDateString('es-AR', { day:'2-digit', month:'short', year:'numeric' }), margin + 146, 53);

      // ── Score ────────────────────────────────────────────────────
      const scoreRgb = score >= 80 ? [34, 197, 94] : score >= 60 ? [245, 158, 11] : [239, 68, 68];
      let y = 70;
      doc.setFillColor(...scoreRgb);
      doc.roundedRect(margin, y, contentW, 14, 3, 3, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text(`${score}/100`, W / 2 - doc.getTextWidth(`${score}/100`) / 2, y + 10);
      y += 18;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...scoreRgb);
      const msg = getScoreMsg(score);
      doc.text(msg, W / 2 - doc.getTextWidth(msg) / 2, y);
      y += 10;

      // ── Stats cards ──────────────────────────────────────────────
      const cardW = (contentW - 8) / 3;
      const cards = [
        { label: 'Tiempo total',  value: `${total_seconds}s`,    color: [129, 140, 248] },
        { label: 'Tiempo ideal',  value: `${benchmark_seconds}s`, color: [34, 197, 94] },
        { label: 'Errores',       value: `${total_errors}`,       color: total_errors > 0 ? [239, 68, 68] : [34, 197, 94] },
      ];
      cards.forEach((card, i) => {
        const x = margin + i * (cardW + 4);
        doc.setFillColor(...card.color.map(c => Math.round(c * 0.12 + 235)));
        doc.roundedRect(x, y, cardW, 20, 3, 3, 'F');
        doc.setTextColor(...card.color);
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text(card.value, x + cardW / 2 - doc.getTextWidth(card.value) / 2, y + 12);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 116, 139);
        doc.text(card.label, x + cardW / 2 - doc.getTextWidth(card.label) / 2, y + 18);
      });
      y += 26;

      // Banner tiempo
      const timeDiffLocal = total_seconds - benchmark_seconds;
      const isOver = timeDiffLocal > 0;
      doc.setFillColor(...(isOver ? [254, 226, 226] : [220, 252, 231]));
      doc.roundedRect(margin, y, contentW, 9, 2, 2, 'F');
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...(isOver ? [239, 68, 68] : [22, 163, 74]));
      const diffText = isOver ? `+${timeDiffLocal}s sobre el tiempo ideal` : `${Math.abs(timeDiffLocal)}s por debajo del tiempo ideal`;
      doc.text(diffText, W / 2 - doc.getTextWidth(diffText) / 2, y + 6.2);
      y += 15;

      // ── Detalle por paso ─────────────────────────────────────────
      doc.setFillColor(239, 68, 68);
      doc.rect(margin, y, contentW, 8, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('DETALLE POR PASO', margin + 3, y + 5.5);
      y += 9;

      // Encabezados
      doc.setFillColor(241, 245, 249);
      doc.rect(margin, y, contentW, 7, 'F');
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(100, 116, 139);
      doc.text('#', margin + 3, y + 5);
      doc.text('Paso', margin + 12, y + 5);
      doc.text('Tiempo', margin + contentW - 26, y + 5);
      doc.text('Estado', margin + contentW - 12, y + 5);
      y += 7;

      (item_times || []).forEach((it, idx) => {
        if (y > 272) { doc.addPage(); y = 20; }
        const rowH = 9;
        const isError = it.is_order_error;
        const isSlow  = false; // No mostrar LENTO en el PDF

        if (idx % 2 === 0) {
          doc.setFillColor(248, 250, 252);
          doc.rect(margin, y, contentW, rowH, 'F');
        }
        if (isError) {
          doc.setFillColor(254, 242, 242);
          doc.rect(margin, y, contentW, rowH, 'F');
          doc.setFillColor(239, 68, 68);
          doc.rect(margin, y, 2, rowH, 'F');
        }

        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(isError ? 239 : 100, isError ? 68 : 116, isError ? 68 : 139);
        doc.text(`${idx + 1}`, margin + 4, y + 6);

        doc.setFont('helvetica', 'normal');
        doc.setTextColor(15, 23, 42);
        let text = it.item_text || '';
        while (doc.getTextWidth(text) > contentW - 52 && text.length > 10) text = text.slice(0, -4) + '...';
        doc.text(text, margin + 12, y + 6);

        const tColor = isError ? [239, 68, 68] : isSlow ? [245, 158, 11] : [22, 163, 74];
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...tColor);
        doc.text(`${it.seconds_taken}s`, margin + contentW - 26, y + 6);

        doc.setFontSize(6);
        if (isError) { doc.setTextColor(239, 68, 68); doc.text('ERROR', margin + contentW - 12, y + 6); }
        else if (isSlow) { doc.setTextColor(245, 158, 11); doc.text('LENTO', margin + contentW - 12, y + 6); }
        else { doc.setFontSize(7); doc.setTextColor(22, 163, 74); doc.text('OK', margin + contentW - 12, y + 6); }

        y += rowH;
      });

      // ── Footer (loop en todas las páginas) ───────────────────────
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFillColor(37, 99, 235);
        doc.rect(0, 282, W, 15, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.text('Condor360 — Emergency Procedure Trainer', margin, 289);
        doc.text('condor360.app', W - margin - doc.getTextWidth('condor360.app'), 289);
        doc.setFontSize(6);
        doc.setTextColor(147, 197, 253);
        doc.text('Entrenamiento sintético generado por Condor360. Herramienta de asistencia para práctica de procedimientos. No reemplaza la instrucción supervisada con instructor calificado ni la documentación oficial del fabricante.', margin, 293, { maxWidth: contentW });
      }
      doc.setTextColor(0, 0, 0);

      const filename = `emergency_${(procedure_title || 'session').replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;
      doc.save(filename);
    } catch (err) {
      console.error('[EmergencyResults] PDF error:', err);
    }
  };

  return (
    <div style={{ position:'fixed', inset:0, zIndex:9999, background:'linear-gradient(135deg,#0a0f1e,#111827)', overflow:'auto' }}>
      <div style={{ maxWidth:480, margin:'0 auto', padding:'24px 20px' }}>

        {/* Score */}
        <motion.div initial={{ scale:0.5, opacity:0 }} animate={{ scale:1, opacity:1 }} transition={{ type:'spring', stiffness:120, delay:0.2 }}
          style={{ textAlign:'center', marginBottom:32 }}>
          <div style={{ fontSize:14, fontWeight:700, color:'rgba(255,255,255,0.4)', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:8 }}>
            {procedure_title}
          </div>
          <div style={{ position:'relative', display:'inline-block' }}>
            <svg width="180" height="180" viewBox="0 0 180 180">
              <circle cx="90" cy="90" r="80" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="10" />
              <motion.circle
                cx="90" cy="90" r="80" fill="none" stroke={scoreColor} strokeWidth="10"
                strokeLinecap="round"
                strokeDasharray={2 * Math.PI * 80}
                initial={{ strokeDashoffset: 2 * Math.PI * 80 }}
                animate={{ strokeDashoffset: 2 * Math.PI * 80 * (1 - score / 100) }}
                transition={{ duration:1.5, delay:0.5, ease:'easeOut' }}
                style={{ transform:'rotate(-90deg)', transformOrigin:'50% 50%' }}
              />
            </svg>
            <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
              <motion.span initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:0.8 }}
                style={{ fontSize:52, fontWeight:900, color:scoreColor, lineHeight:1 }}>
                {score}
              </motion.span>
              <span style={{ fontSize:14, fontWeight:600, color:'rgba(255,255,255,0.4)' }}>/100</span>
            </div>
          </div>
          <motion.p initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }} transition={{ delay:1.2 }}
            style={{ margin:'16px 0 0', fontSize:15, fontWeight:600, color:'rgba(255,255,255,0.7)', lineHeight:1.5 }}>
            {getScoreMsg(score)}
          </motion.p>
        </motion.div>

        {/* Stats cards */}
        <motion.div initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.6 }}
          style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:24 }}>
          {[
            { icon: Clock,         value:`${total_seconds}s`,    label:'Tiempo',       color:'#818cf8' },
            { icon: Award,         value:`${benchmark_seconds}s`, label:'Tiempo ideal', color:'#22c55e' },
            { icon: AlertTriangle, value:`${total_errors}`,      label:'Errores',      color: total_errors > 0 ? '#ef4444' : '#22c55e' },
          ].map((c, i) => (
            <div key={i} style={{ background:'rgba(255,255,255,0.05)', borderRadius:16, padding:'16px 12px', textAlign:'center', border:'1px solid rgba(255,255,255,0.08)' }}>
              <c.icon size={20} color={c.color} style={{ marginBottom:6 }} />
              <div style={{ fontSize:22, fontWeight:800, color: i === 2 && order_errors > 0 ? '#ef4444' : 'white' }}>{c.value}</div>
              <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', marginTop:2 }}>{c.label}</div>
            </div>
          ))}
        </motion.div>

        {/* Time difference */}
        <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:0.8 }}
          style={{ padding:'12px 16px', borderRadius:14, background: timeDiff > 0 ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)', border:`1px solid ${timeDiff > 0 ? 'rgba(239,68,68,0.2)' : 'rgba(34,197,94,0.2)'}`, textAlign:'center', marginBottom:24 }}>
          <span style={{ fontSize:14, fontWeight:700, color: timeDiff > 0 ? '#ef4444' : '#22c55e' }}>
            {timeDiff > 0 ? `+${timeDiff}s sobre el tiempo ideal` : `${Math.abs(timeDiff)}s por debajo del tiempo ideal`}
          </span>
        </motion.div>

        {/* Item times */}
        {item_times && item_times.length > 0 && (
          <motion.div initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} transition={{ delay:1 }}
            style={{ marginBottom:24 }}>
            <h4 style={{ margin:'0 0 12px', fontSize:14, fontWeight:700, color:'rgba(255,255,255,0.7)' }}>
              Detalle por paso
            </h4>
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              {item_times.map((it, idx) => (
                <div key={idx}
                  style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', borderRadius:12,
                    background: it.is_order_error ? 'rgba(239,68,68,0.08)' : 'rgba(255,255,255,0.03)',
                    border:`1px solid ${it.is_order_error ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.06)'}` }}>
                  <span style={{ fontSize:12, fontWeight:800, color: it.is_order_error ? '#ef4444' : 'rgba(255,255,255,0.4)', minWidth:20, textAlign:'center' }}>
                    {idx + 1}
                  </span>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:'white', lineHeight:1.3 }}>{it.item_text}</div>
                    {it.is_order_error && (
                      <span style={{ fontSize:10, fontWeight:700, color:'#ef4444' }}>ERROR DE ORDEN</span>
                    )}
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontSize:14, fontWeight:700, color:(it.item_benchmark > 0 && it.seconds_taken > it.item_benchmark * 1.5) ? '#f59e0b' : 'white' }}>
                      {it.seconds_taken}s
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Actions */}
        <motion.div initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} transition={{ delay:1.3 }}
          style={{ display:'flex', flexDirection:'column', gap:10, paddingBottom:'max(24px, env(safe-area-inset-bottom))' }}>
          <button onClick={onRetry}
            style={{ width:'100%', padding:'16px', borderRadius:16, border:'none', background:'linear-gradient(135deg,#ef4444,#f97316)', color:'white', fontWeight:800, fontSize:16, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:8, boxShadow:'0 6px 20px rgba(239,68,68,0.3)' }}>
            <RotateCcw size={18} /> Practicar de nuevo
          </button>
          <button onClick={exportPDF}
            style={{ width:'100%', padding:'14px', borderRadius:14, border:'1px solid rgba(255,255,255,0.15)', background:'rgba(255,255,255,0.05)', color:'white', fontWeight:700, fontSize:14, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
            <Download size={16} /> Exportar PDF
          </button>
          <button onClick={onClose}
            style={{ width:'100%', padding:'14px', borderRadius:14, border:'none', background:'rgba(255,255,255,0.08)', color:'rgba(255,255,255,0.6)', fontWeight:600, fontSize:14, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
            <ArrowLeft size={16} /> Volver al panel
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default EmergencyResults;
